import { useMemo } from "react";
import { Building2, MapPinned, Sprout, Leaf } from "lucide-react";

import { formatNumber } from "@/utils/numberFormat";

import CustomBadge from "@/components/desktop/badge/CustomBadge";

import { getApplicationOrderStateVariant } from "@/constants/applicationOrderStates";
import { getApplicationOrderTypeVariant } from "@/constants/applicationOrderTypes";

import OAActionsDropdown from "../../components/actions/OAActionsDropdown";
import OAProductsTable from "../../components/products/OAProductsTable";
import OATooltip from "../../components/tooltip/oa_tooltip/OATooltip";
import OAValorizationCell from "../../components/tooltip/oa_valorization_cell/OAValorizationCell";

import styles from "./useOAColumns.module.scss";

const checkboxStyle = { width: "16px", height: "16px", cursor: "pointer", accentColor: "#82bc44" };

export function useOAColumns(selectionState, navigate, customer = {}, actionContext = {}) {
	const { selectedRows = {}, onRowSelect, onSelectAll, allRowsCount = 0 } = selectionState;
	const showInternalCode = Boolean(customer?.use_code_oa);
	const { permissions = [], canShowPrice = false } = actionContext;

	return useMemo(() => {
		const selectedCount = Object.keys(selectedRows).length;
		const allSelected = allRowsCount > 0 && selectedCount === allRowsCount;

		const columns = [
			{
				id: "select",
				Header: () => <input type="checkbox" style={checkboxStyle} checked={allSelected} onChange={(event) => onSelectAll(event.target.checked)} />,
				Cell: ({ row }) => (
					<div className={styles.selectCell}>
						<input
							type="checkbox"
							style={checkboxStyle}
							checked={Boolean(selectedRows[String(row.index)])}
							onChange={(event) => onRowSelect(String(row.index), row.original, event.target.checked)}
						/>
					</div>
				),
				disableSortBy: true,
				width: "3%",
			},
			{
				Header: "OA",
				accessor: "code",
				width: "10%",
				mobileSummary: true,
				Cell: ({ row }) => (
					<OATooltip
						tooltip={row.original.tooltip}
						applicationOrdersStateId={row.original.application_orders_state_id}
						rowIndex={row.index}
						totalRows={allRowsCount}
					>
						<div className={styles.oaCard}>
							<div className={styles.oaCode}>N° {row.original.code || "-"}</div>
							<div className={styles.oaBadges}>
								<CustomBadge
									variant={getApplicationOrderTypeVariant(
										Number(row.original.application_order_type_id_customer || row.original.application_order_type_id),
									)}
									size="sm"
								>
									{row.original.application_order_type_customer_name || row.original.application_order_type_name || "-"}
								</CustomBadge>
								<CustomBadge variant={getApplicationOrderStateVariant(Number(row.original.application_orders_state_id))} size="sm">
									{row.original.application_orders_state_name || "-"}
								</CustomBadge>
							</div>
							{showInternalCode && (
								<span className={styles.oaInternalCode}>
									Cód. Interno: <strong>{row.original.internal_code || "-"}</strong>
								</span>
							)}
						</div>
					</OATooltip>
				),
				disableSortBy: true,
			},
			{
				Header: "Campo",
				accessor: "estate_parent_name",
				width: "8%",
				Cell: ({ value }) => <div className={styles.oaField}>{value || "-"}</div>,
			},
			{
				Header: "Fecha",
				accessor: "application_order_date",
				width: "8%",
				Cell: ({ value }) => {
					if (!value) return "-";
					return new Date(value).toLocaleDateString("es-CL");
				},
			},
			{
				Header: "Centros de Costo",
				accessor: "business_centers",
				width: "18%",
				Cell: ({ row }) => (
					<div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "0.35rem" }}>
						{(row.original.business_centers || []).length === 0 && <span>-</span>}
						{(row.original.business_centers || []).map((businessCenter, index) => {
							const parentName = businessCenter.business_center_parent_name || "";
							const sectorName = businessCenter.name || "-";
							const showParent = parentName && parentName !== sectorName;

							return (
								<div key={`${businessCenter.id || businessCenter.name || "bc"}-${index}`} className={styles.centerCard}>
									{showParent && (
										<div className={styles.centerHeader}>
											<Building2 size={14} />
											<span>{parentName}</span>
										</div>
									)}
									<div className={styles.centerName}>
										<MapPinned size={14} color="#0f766e" />
										<span>{sectorName}</span>
									</div>
									<div className={styles.centerMeta}>
										<span className={styles.oaChip}>
											<Leaf size={12} style={{ marginRight: "0.25rem" }} />
											{businessCenter.specie || "Sin especie"}
										</span>
										<span className={styles.oaChip}>
											<Sprout size={12} style={{ marginRight: "0.25rem" }} />
											{businessCenter.variety || "Sin variedad"}
										</span>
									</div>
								</div>
							);
						})}
					</div>
				),
				disableSortBy: true,
			},
			{
				Header: "Total Ha",
				accessor: "areas_sum",
				width: "5%",
				meta: { cellClassName: "tdRight" },
				Cell: ({ value }) => formatNumber(Number(value || 0)),
				disableSortBy: true,
			},
			{
				Header: "Mojamiento (L/ha)",
				accessor: "application_orders_machineries_wetness",
				width: "6%",
				meta: { cellClassName: "tdRight" },
				Cell: ({ value }) => formatNumber(Number(value || 0)),
				disableSortBy: true,
			},
			...(canShowPrice
				? [
						{
							Header: "$Valor",
							accessor: "details",
							id: "valorizacion",
							width: "8%",
							disableSortBy: true,
							Cell: ({ row }) => (
								<OAValorizationCell
									details={row.original.details || []}
									stateId={row.original.application_orders_state_id}
									rowIndex={row.index}
									totalRows={allRowsCount}
								/>
							),
						},
					]
				: []),
			{
				Header: "Productos",
				accessor: "details",
				width: "37%",
				Cell: ({ value, row }) => (
					<OAProductsTable
						details={value || []}
						wetness={row.original.application_orders_machineries_wetness}
						machinedLiters={row.original.application_orders_machineries_machined}
						applicationOrderTypeId={row.original.application_order_type_id}
					/>
				),
				disableSortBy: true,
			},
			{
				Header: () => <div className={styles.actionsHeader}>Acción</div>,
				id: "actions",
				width: "5%",
				disableSortBy: true,
				Cell: ({ row }) => (
					<div className={styles.actionsCell}>
						<OAActionsDropdown item={row.original} navigate={navigate} permissions={permissions} />
					</div>
				),
			},
		];

		return columns;
	}, [allRowsCount, canShowPrice, navigate, onRowSelect, onSelectAll, permissions, selectedRows, showInternalCode]);
}
